/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Field, ErrorMessage } from "formik";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Toggle from "react-toggle";

/* Helpers, constants, custom methods, internal libraries */
import { POLICY_ROUTE } from "../../../constants/routes.js";
import { AIRLINES } from "../../../constants/appConstants.js";
import { useHasPermission } from "../../../hooks/useHasPermission.js";
import { getBasicEconomyLink, getCurrencySymbol, getCurrencyLocale } from '../../../helpers/general.js';
import { useUserContext } from "../../../contexts/userContext.js";

/* Components */
import UCButton from "../../../components/buttons/index.js";
import UCToggle from "../../../components/uctoggle/index.js";
import UCSelect from "../../../components/ucselect/index.js";
import { ReactComponent as InfoIconSvg } from "../../../resources/images/info-icon.svg";
import { ReactComponent as DoubleChevronRightIcon } from "../../../resources/images/chevron-double-right.svg";

/* Styles */
import styles, { selectField, currencyDropdownStyles } from '../styles.js';
import generalStyles from "../../../resources/styles/general.js";
import { colors } from "../../../constants/colors.js";
import "react-toggle/style.css";
import { usePolicyService } from '../../../services/policyService';

const PolicySettingsCard = ({
  formRef,
  values,
  handleFormSubmit,
  setFieldValue,
  showSaveBtn,
  userHasEditPermission
}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const selectFieldStyles = selectField(theme);
  const navigate = useNavigate();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const { getCurrencyOptions } = usePolicyService();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode
  const [showTooltip, setShowTooltip] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const optionCurrencies = await getCurrencyOptions('O_COMPANYSETTINGS_CURRENCY');
      const opts = (optionCurrencies?.options[0]?.options || []).map((opt) => ({
        value: opt.optionValue,
        label: opt.optionText,
      }));
      setCurrencyOptions(opts);
    })();
  }, []);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    navigate("../" + POLICY_ROUTE);
  };

  const allowRefundableOptions = [
    {
      value: "true",
      label: <FormattedMessage id="editPolicy.allowRefundableOptions.allowed" />,
    },
    {
      value: "false",
      label: <FormattedMessage id="editPolicy.allowRefundableOptions.notAllowed" />
    },
  ];

  const requireRefundableOptions = [
    {
      value: "true",
      label: <FormattedMessage id="editPolicy.requireRefundableOptions.required" />
    },
    {
      value: "false",
      label: <FormattedMessage id="editPolicy.requireRefundableOptions.notRequired" />
    },
  ];

  const advancePurchaseOptions = Array.from({ length: 60 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (
      <FormattedMessage
        id={
          i === 0
            ? "editPolicy.advancePurchaseOptions.day"
            : "editPolicy.advancePurchaseOptions.days"
        }
        values={{ count: i + 1 }}
      />
    ),
  }));

  return (
    <div className={classes.policyContainer}>
      <div className={classes.policySettingsHeader}>
        <div>
          <h3>
            <FormattedMessage
              id={
                providerCode === AIRLINES.AS
                  ? 'editPolicy.settingsTitle.AS'
                  : 'editPolicy.settingsTitle'
              }
            />
          </h3>
          <p>
            <FormattedMessage
              id={
                providerCode === AIRLINES.AS
                  ? 'editPolicy.settingsSubtitle.AS'
                  : 'editPolicy.settingsSubtitle'
              }
            />
          </p>
        </div>
        {userHasEditPermission && (
          <div className={classes.btnContainer}>
            <UCButton
              type="default"
              className={classes.cancelBtn}
              onClick={cancelUpdate}
              text={<FormattedMessage id="editPolicy.cancelBtn" />}
            />
            <UCButton
              type="primary"
              className={classes.saveBtn}
              onClick={handleFormSubmit}
              text={<FormattedMessage id="editPolicy.saveBtn" />}
              disabled={!formRef.current?.dirty && !showSaveBtn}
              spinner={{
                name: 'editPolicySpinner',
                size: 20,
                color: colors.white,
              }}
            />
          </div>
        )}
      </div>

      <div className={classes.policyCard}>
        {(providerCode === AIRLINES.AM || providerCode === AIRLINES.AS) && (
          <>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editPolicy.settingsMaxPriceTitle" />
                </h6>
                <p>
                  <FormattedMessage id="editPolicy.settingsMaxPriceSubtitle" />
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div className={classes.priceInputWrapper}>
                  <Field type="number" name="maxTripFare" id="maxTripFare">
                    {({ field, form }) => (
                      <div>
                        <div className={classes.priceInputUnified}>
                          <div className={classes.priceInputContent}>
                            <span className={classes.priceInputDollar}>
                              {getCurrencySymbol(values.currency || 'USD')}
                            </span>
                            <input
                              {...field}
                              type="text"
                              placeholder="0"
                              className={classes.priceInputField}
                              value={
                                field.value !== '' &&
                                field.value !== undefined &&
                                field.value !== null
                                  ? new Intl.NumberFormat(
                                      getCurrencyLocale(values.currency || 'USD'),
                                      {
                                        maximumFractionDigits: 0,
                                      },
                                    ).format(Number(field.value))
                                  : ''
                              }
                              onChange={(e) => {
                                const raw = e.target.value.replace(/,/g, '');
                                if (raw === '' || (/^\d+$/.test(raw) && raw.length <= 5)) {
                                  form.setFieldValue('maxTripFare', raw);
                                }
                              }}
                            />
                          </div>
                          <div className={classes.priceInputDropdownSection}>
                            <Field name="currency">
                              {({ field: currencyField }) => (
                                <UCSelect
                                  key={currencyOptions.length}
                                  {...currencyField}
                                  styles={currencyDropdownStyles(selectFieldStyles, theme)}
                                  options={currencyOptions}
                                  value={values.currency || 'USD'}
                                  onChange={(item) => form.setFieldValue('currency', item.value)}
                                  isSearchable={false}
                                />
                              )}
                            </Field>
                          </div>
                        </div>
                        <div className={classes.errorWrapper}>
                          <ErrorMessage name="maxTripFare" component="span" />
                        </div>
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.settingsBasicTitle.AS'
                    : 'editPolicy.settingsBasicTitle'
                }
              />
            </h6>
            <p>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.settingsBasicSubtitle.AS'
                    : 'editPolicy.settingsBasicSubtitle'
                }
              />
            </p>
          </div>
          <div className={classes.policyRowAction}>
            <div className={classes.toggle}>
              <Toggle
                icons={false}
                onChange={(e) => {
                  setFieldValue('allowBasicFares', e.target.checked);
                }}
                onClick={(e) => e.currentTarget.blur()}
                checked={values.allowBasicFares}
                disabled={values.requiredRefundableFares}
              />
              <div className={classes.toggleLabel}>
                <span>
                  <FormattedMessage
                    id={
                      providerCode === AIRLINES.AS
                        ? 'editPolicy.settingsAllowToggle.AS'
                        : 'editPolicy.settingsAllowToggle'
                    }
                  />
                </span>
                {providerCode !== AIRLINES.AM && (
                <div>
                  <OverlayTrigger
                    placement="top"
                    show={showTooltip}
                    overlay={
                      <Tooltip
                        className={`${general.main} ${classes.tooltip}`}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className={classes.tooltipTitle}>
                          <FormattedMessage id="editPolicy.allowTooltipTitle" />
                        </div>
                        <div className={classes.tooltipDescription}>
                          <FormattedMessage id="editPolicy.allowTooltipSubtitle" />
                          <Link to={getBasicEconomyLink(providerCode)} target="_blank">
                            <FormattedMessage id="editPolicy.allowTooltipLink" />{' '}
                            <DoubleChevronRightIcon />
                          </Link>
                        </div>
                      </Tooltip>
                    }
                  >
                    <InfoIconSvg
                      className={classes.infoIcon}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  </OverlayTrigger>
                </div>
                  )}
              </div>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.settingsAdvancedTitle.AS'
                    : 'editPolicy.settingsAdvancedTitle'
                }
              />
            </h6>
            <p>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.settingsAdvancedSubtitle.AS'
                    : 'editPolicy.settingsAdvancedSubtitle'
                }
              />
            </p>
          </div>
          <div className={classes.policyRowAction}>
            <div>
              <UCToggle
                label={
                  <div className={classes.toggleLabel}>
                    <span>
                      <FormattedMessage
                        id={
                          providerCode === AIRLINES.AS
                            ? 'editPolicy.settingsRequireToggle.AS'
                            : 'editPolicy.settingsRequireToggle'
                        }
                      />
                    </span>
                    {providerCode !== AIRLINES.AM && (
                    <div>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip className={`${general.main} ${classes.tooltip}`}>
                            <div className={classes.tooltipTitle}>
                              <FormattedMessage id="editPolicy.requireTooltipTitle" />
                            </div>
                            <div className={classes.tooltipDescription}>
                              <FormattedMessage id="editPolicy.requireTooltipSubtitle" />
                            </div>
                          </Tooltip>
                        }
                      >
                        <InfoIconSvg className={classes.infoIcon} />
                      </OverlayTrigger>
                    </div>
                      )}
                  </div>
                }
                icons={null}
                onChange={(e) => {
                  setFieldValue('requiredAdvancePurchase', e.target.checked);
                  setFieldValue('advancePurchase', e.target.checked ? '1' : null);
                }}
                defaultChecked={values.requiredAdvancePurchase}
              />
              {values.requiredAdvancePurchase && (
                <Field type="select" name="advancePurchase" id="advancePurchase">
                  {({ form }) => (
                    <div>
                      <UCSelect
                        styles={selectField}
                        className={classes.selectAdvancedField}
                        options={advancePurchaseOptions}
                        value={values.advancePurchase}
                        defaultValue={advancePurchaseOptions.find(
                          (item) => item.value === values.advancePurchase,
                        )}
                        onChange={(item) => form.setFieldValue('advancePurchase', item.value)}
                      />
                    </div>
                  )}
                </Field>
              )}
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage id="editPolicy.settingsRefundableTitle" />
            </h6>
            <p>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.settingsRefundableSubtitle.AS'
                    : 'editPolicy.settingsRefundableSubtitle'
                }
              />
            </p>
          </div>
          <div className={classes.policyRowAction}>
            <div className={classes.policySelectWrapper}>
              <Field type="select" name="allowRefundableFares" id="allowRefundableFares">
                {({ form }) => (
                  <div>
                    <UCSelect
                      styles={selectField}
                      className={classes.selectField}
                      options={allowRefundableOptions}
                      value={String(values.allowRefundableFares)}
                      defaultValue={allowRefundableOptions.find(
                        (item) => item.value === String(values.allowRefundableFares),
                      )}
                      onChange={(item) => {
                        form.setFieldValue('allowRefundableFares', item.value === 'true');
                        item.value !== 'true' &&
                          form.setFieldValue('requiredRefundableFares', false);
                      }}
                    />
                  </div>
                )}
              </Field>
              {values.allowRefundableFares && (
                <Field type="select" name="requiredRefundableFares" id="requiredRefundableFares">
                  {({ form }) => (
                    <div>
                      <UCSelect
                        styles={selectField}
                        className={classes.selectField}
                        options={requireRefundableOptions}
                        value={String(values.requiredRefundableFares)}
                        defaultValue={requireRefundableOptions.find(
                          (item) => item.value === String(values.requiredRefundableFares),
                        )}
                        onChange={(item) => {
                          form.setFieldValue('requiredRefundableFares', item.value === 'true');
                          item.value === 'true' && form.setFieldValue('allowBasicFares', false);
                        }}
                      />
                    </div>
                  )}
                </Field>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicySettingsCard;
